import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useLanguage, routeMapping } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { servicesImage } from '../data/mockData';
import SEOHead from '../components/SEOHead';

const ExpertiseBilingual = () => {
  const { language, t } = useLanguage();
  const expertise = translations.expertise;
  const nav = translations.nav;
  
  const [expandedServices, setExpandedServices] = useState({});
  
  const langPrefix = `/${language}`;
  const getRoute = (pageKey) => {
    const route = routeMapping[language][pageKey];
    return `${langPrefix}${route ? '/' + route : ''}`;
  };
  
  const toggleService = (serviceId) => {
    setExpandedServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  const services = [
    { id: 1, title: expertise.service1Title, desc: expertise.service1Desc, details: expertise.service1Details },
    { id: 2, title: expertise.service2Title, desc: expertise.service2Desc, details: expertise.service2Details },
    { id: 3, title: expertise.service3Title, desc: expertise.service3Desc, details: expertise.service3Details },
    { id: 4, title: expertise.service4Title, desc: expertise.service4Desc, details: expertise.service4Details }
  ];

  const clientTypes = [
    { title: expertise.clientType1, desc: expertise.clientType1Desc },
    { title: expertise.clientType2, desc: expertise.clientType2Desc },
    { title: expertise.clientType3, desc: expertise.clientType3Desc },
    { title: expertise.clientType4, desc: expertise.clientType4Desc }
  ];

  return (
    <div>
      <SEOHead 
        title={t(expertise.seoTitle)}
        description={t(expertise.seoDescription)}
      />
      
      {/* Hero Section */}
      <section className="section-padding-large" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="grid-two-column" style={{ gap: '100px' }}>
            <div>
              <p className="meta-text" style={{ marginBottom: '24px', color: 'var(--gold-accent)' }}>
                {t(expertise.heroSubtitle)}
              </p>
              <h1 className="hero-large" style={{ marginBottom: '32px' }}>
                {t(expertise.heroTitle)}
              </h1>
              <p className="body-large" style={{ 
                marginBottom: '24px',
                color: 'var(--text-secondary)'
              }}>
                {t(expertise.heroDescription)}
              </p>
              <p className="body-regular" style={{ 
                color: 'var(--text-light)',
                fontStyle: 'italic'
              }}>
                {t(expertise.privateNote)}
              </p>
            </div>
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_c17ab4df-56cb-4633-98aa-b5d05bd5d93e/artifacts/2rbifocs_iStock-2214553404.jpg"
                alt={t(expertise.heroTitle)}
                style={{
                  width: '100%',
                  height: '500px',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Services Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="heading-1">{t(expertise.coreServicesTitle)}</h2>
          </div>
          
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            {services.map((service, idx) => (
              <div 
                key={service.id}
                style={{
                  borderBottom: idx < services.length - 1 ? '1px solid var(--border-light)' : 'none',
                  paddingBottom: '40px',
                  marginBottom: '40px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
                  <span className="meta-text" style={{ color: 'var(--gold-accent)', marginTop: '4px' }}>
                    0{service.id}
                  </span>
                  <div style={{ flex: 1 }}>
                    <h3 className="heading-2" style={{ marginBottom: '16px' }}>
                      {t(service.title)}
                    </h3>
                    <p className="body-regular" style={{ 
                      color: 'var(--text-secondary)',
                      marginBottom: '16px'
                    }}>
                      {t(service.desc)}
                    </p>
                    
                    <button
                      onClick={() => toggleService(service.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: 'var(--gold-accent)',
                        fontSize: '14px',
                        fontFamily: "'Suisse Regular', sans-serif",
                        padding: 0
                      }}
                    >
                      {expandedServices[service.id] ? t(expertise.showLess) : t(expertise.readMore)}
                      <ChevronDown 
                        size={16} 
                        style={{ 
                          transform: expandedServices[service.id] ? 'rotate(180deg)' : 'rotate(0)',
                          transition: 'transform 0.2s ease'
                        }} 
                      />
                    </button>
                    
                    {expandedServices[service.id] && (
                      <ul style={{ 
                        marginTop: '20px',
                        paddingLeft: '20px',
                        color: 'var(--text-secondary)'
                      }}>
                        {t(service.details).map((detail, i) => (
                          <li key={i} className="body-regular" style={{ marginBottom: '8px' }}>
                            {detail}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <p className="meta-text" style={{ marginBottom: '16px', color: 'var(--gold-accent)' }}>
              {t(expertise.whoWeServeSubtitle)}
            </p>
            <h2 className="heading-1" style={{ marginBottom: '24px' }}>
              {t(expertise.whoWeServeTitle)}
            </h2>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              {t(expertise.whoWeServeDesc)}
            </p>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))',
            gap: '48px',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            {clientTypes.map((client, idx) => (
              <div key={idx}>
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                  {t(client.title)}
                </h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {t(client.desc)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
            <h2 className="heading-1" style={{ marginBottom: '24px' }}>
              {t(expertise.ctaTitle)}
            </h2>
            <p className="body-large" style={{ 
              marginBottom: '40px',
              color: 'var(--text-secondary)'
            }} dangerouslySetInnerHTML={{ __html: t(expertise.ctaDescription) }} />
            <Link to={getRoute('contact')} className="btn-primary" data-testid="cta-btn">
              {t(nav.cta)}
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Block */}
      <section style={{ padding: '40px 0', background: 'var(--bg-primary)' }}>
        <div className="container">
          <p style={{ 
            fontSize: '12px',
            lineHeight: 1.7,
            color: 'var(--text-light)',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {t(expertise.seoBlock)}
          </p>
        </div>
      </section>
    </div>
  );
};

export default ExpertiseBilingual;
